import { CircleDollarSign, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRefusalText, type RefusalSubject } from '../lib/useRefusal';
import type { Refusal, RefusalFix } from '../lib/refusals';

/**
 * A refusal the venue can read, and — where there is one — the way out of it.
 *
 * Two things it deliberately does NOT do:
 *
 *  - **Print what the server said.** Not as a fallback, not "just for the codes
 *    we do not know", not in small grey text underneath. The server's sentence
 *    is one language and sometimes a route; see `lib/refusals.ts`. It travels
 *    instead on `data-server-message`, which a developer reads in the inspector
 *    and which no user, screen reader or copy-paste of the visible text can
 *    reach.
 *  - **Offer one generic "fix pricing" button.** The three session-start
 *    refusals are three different mistakes and the button label and destination
 *    differ for each; `refusalSentence` decides which, and the caller decides
 *    what "attach a list" or "open the card" means from where it is standing.
 */
export function RefusalNotice({
  refusal,
  names,
  tone = 'warning',
  onFix,
}: {
  refusal: Refusal;
  names?: RefusalSubject;
  /** Amber for "you cannot do this yet", red for "that save was rejected". */
  tone?: 'warning' | 'danger';
  /** Absent where the caller has nowhere to send them; the button is then withheld. */
  onFix?: (fix: Exclude<RefusalFix, null>) => void;
}) {
  const text = useRefusalText();
  const rendered = text(refusal, names);

  const skin =
    tone === 'danger'
      ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
      : 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100';

  return (
    <div
      role="alert"
      // Not rendered, not in `textContent`, not selectable — the inspector only.
      data-server-message={rendered.serverMessage || undefined}
      className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${skin}`}
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="space-y-2">
        <p className="font-medium" dir="auto">
          {rendered.title}
        </p>
        <p dir="auto">{rendered.body}</p>
        {rendered.fix && rendered.fixLabel && onFix && (
          <Button size="sm" onClick={() => onFix(rendered.fix!)}>
            <CircleDollarSign className="h-4 w-4" />
            {rendered.fixLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
